<template>
    <div class="flex flex-row align-items-start gap-2 xl:flex-row lg:flex-row md:flex-row px-3 py-2"
        :class="(screenSize === 'xs' || screenSize === 'sm') ? 'overflow-x-scroll' : ''">
        <SelectButton v-model="set" :options="sets" optionLabel="name" multiple aria-labelledby="multiple">
            <template #option="slotProps">
                <span class="text-xs py-1 white-space-nowrap">{{ slotProps.option.name }}</span>
            </template>
        </SelectButton>
        <Button :label="(screenSize === 'xs' || screenSize === 'sm') ? '' : 'Сбросить'" 
            icon="pi pi-filter" size="small" @click="resetFilter" text severity="info" class="mt-1"/>
        <Button :label="(screenSize === 'xs' || screenSize === 'sm') ? '' : 'Добавить'" 
            icon="pi pi-plus" size="small" @click="openAddPopup" text severity="info" class="mt-1"/>
    </div>

    <DataTable v-if='banks' :value="banks" responsiveLayout="scroll" ref="dt" dataKey="id"
        :rows="20" :rowsPerPageOptions="[20, 50, 100, 200, 300]" :totalRecords="totalRecords" paginator
        @page="onPage($event)" @sort="onSort($event)" :loading="loading" 
        @filter="onFilter($event)" v-model:filters="filters" filterDisplay="menu" :globalFilterFields="['id', 'active', 'type', 'code', 'currency_code']" >

        <Column field="id" header="" filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
            <template #header>
                <span class="text-xs">ID</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-sm flex flex-row" @click="copy(data.id)">
                    <span class="text-xs flex my-auto overflow-hidden" style="max-width:4em">
                        <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                            {{ data.id }}
                        </span>
                    </span>...
                    <span class="flex pi pi-copy ml-2 text-500 text-xs cursor-pointer"></span>
                </span>
            </template>
        </Column>

        <Column field="active" :showFilterMenu="true" :showFilterMatchModes="false">
            <template #header>
                <span class="text-xs">Акт.</span>
            </template>
            <template #body="{ data }">
                <div class="flex flex-row gap-2 align-items-center">
                    <span class="text-white font-medium text-xs p-1 border-round-md" :class="(data.active) ? 'surface-500' : 'bg-orange-400'">
                        {{ (data.active) ? 'да' : 'нет' }}
                    </span>
                    <Button icon="pi pi-pencil" @click="edit(data.code)" class="flex border-round-lg border-none" size="small" severity="info"/>
                </div>
            </template>
        </Column>

        <Column field="type" :showFilterMenu="true" :showFilterMatchModes="false">
            <template #header>
                <span class="text-xs">Тип</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <Dropdown v-model="filterModel.value" @change="filterCallback()" :options="types" optionLabel="label" optionValue="value" placeholder="Все" class="p-column-filter" :showClear="false">
                    <template #option="slotProps">
                        <div class="inline-block py-1 px-2 text-xs border-round-md font-semibold surface-300 cursor-pointer font-medium align-items-center" >
                            <span class="flex">{{slotProps.option.label}}</span>
                        </div>
                    </template>
                </Dropdown>
            </template>
            <template #body="{ data }">
                <span class="text-white font-medium text-xs p-1 border-round-md surface-500">
                    {{ getType(data.type) }}
                </span>
            </template>
        </Column>

        <Column field="title">
            <template #header>
                <span class="text-xs">Имя</span>
            </template>
            <template #body="{ data }">
                <div v-if="data.code" class="flex flex-row text-sm m-0">
                    <img :src="getBankImage(data.code)" class="mr-3" style="max-width: 22px;">
                    <div class="flex flex-column">
                        <span class="white-space-nowrap" >{{ getBankTitle(data.code) }}</span>
                    </div>
                </div>
            </template>
        </Column>

        <Column field="code" :showFilterMenu="true" :showFilterMatchModes="false">
            <template #header>
                <span class="text-xs">Код</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div v-if="data.code" class="flex flex-row gap-2 align-items-center">
                    <div class="flex text-sm text-900">
                        <span>{{ data.code }}</span>
                    </div>
                </div>
            </template>
        </Column>
        
        <Column field="sort" sortable filterField="date_update" :showFilterMenu="true" :showFilterMatchModes="false">
            <template #header>
                <span class="text-xs">Сорт.</span>
            </template>
            <template #body="{ data }">
                <p v-if="data.sort" class="text-xs white-space-nowrap m-0">
                    {{ data.sort }}
                </p>
            </template>
        </Column>

        <Column field="currency_code" :showFilterMenu="true" :showFilterMatchModes="false">
            <template #header>
                <span class="text-xs">Валюта</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <Select v-model="filterModel.value" @change="filterCallback()" :options="currenciesCodes" placeholder="Все" class="p-column-filter uppercase" :showClear="true" filter>
                    <template #option="slotProps">
                        <div class="inline-block py-1 px-2 text-xs border-round-md font-semibold surface-300 cursor-pointer font-medium align-items-center" >
                            <span class="flex uppercase">{{slotProps.option}}</span>
                        </div>
                    </template>
                </Select>
            </template>
            <template #body="{ data }">
                <div class="flex flex-row align-items-center gap-2" >
                    <AvatarGroup v-if="data.currency_code">
                        <Avatar :image="getCurImage(data.currency_code)" class="surface-0  border-2 border-100" shape="circle" size="small"/>
                    </AvatarGroup>
                    <p class="text-xs white-space-nowrap m-0 uppercase">
                        {{ (data.currency_code) ? data.currency_code : '–'}}
                    </p>
                </div>
            </template>
        </Column>

        <Column field="recipient_fields_required" v-if="selectedColumns.includes('Реквизиты')">
            <template #header>
                <span class="text-xs">Реквизиты</span>
            </template>
            <template #body="{ data }">
                <div v-if="data.recipient_fields" class="text-xs white-space-nowrap m-0">
                    <div v-for="field of data.recipient_fields" class="inline-block relative mb-0 p-1 border-2 border-round-md surface-0 mr-2 mb-2" 
                        :class="(data.recipient_fields_required && data.recipient_fields_required.includes(field)) ? 'border-primary' : 'border-200'">
                        <span class="text-xs inline-block white-space-nowrap float-left m-0" v-tooltip.top="field"
                            :class="(data.recipient_fields_required && data.recipient_fields_required.includes(field)) ? 'text-primary' : 'text-600'">
                            {{ field }}
                        </span>
                    </div>
                </div>
            </template>
        </Column>

        <Column field="amount_list" v-if="selectedColumns.includes('Комиссии')">
            <template #header>
                <span class="text-xs">Комиссии</span>
            </template>
            <template #body="{ data }">
                <div v-if="data.comissions" class="text-xs white-space-nowrap m-0">
                    <span v-if="data.comissions.percent">Процент {{ data.comissions.percent }}</span><br>
                    <span v-if="data.comissions.fix">Фикс - {{ data.comissions.fix }} usdt</span>
                </div>
            </template>
        </Column>

        <Column field="amount_list" v-if="selectedColumns.includes('Лимиты')">
            <template #header>
                <span class="text-xs">Лимиты</span>
            </template>
            <template #body="{ data }">
                <div v-if="data.limits" class="text-xs white-space-nowrap m-0">
                    <span v-if="data.limits.min">Мин {{ data.limits.min }}</span><br>
                    <span v-if="data.limits.max">Макс {{ data.limits.max }}</span>
                </div>
            </template>
        </Column>

        <Column field="amount_list" v-if="selectedColumns.includes('Суммы')">
            <template #header>
                <span class="text-xs">Суммы</span>
            </template>
            <template #body="{ data }">
                <div v-if="data.amount_list" class="text-xs white-space-nowrap m-0">
                    <div v-for="amount of data.amount_list" class="inline-block relative mb-0 p-1 border-2 border-200 border-round-md surface-0 mr-2 mb-2" style="line-height:15px">
                        <span class="text-xs inline-block white-space-nowrap float-left m-0" >{{ get_format_amount(amount, data.currency_code) }}</span>
                    </div>
                </div>
            </template>
        </Column>

        <template #footer>
            <span class="text-sm font-medium">
                Всего банков {{ totalRecords }}
            </span>
        </template>

    </DataTable>
    <Skeleton height="2rem" v-else />

    <!--ИЗМЕНЕНИЕ-->
    <Dialog class="flex flex-column border-none" modal
        v-model:visible="editPopup"
        :position="screenSize=='xs' ? 'bottom' : 'center'"
        :style="(screenSize=='xs' || screenSize=='sm' || screenSize=='md') ? {width: '100%!important', height: '100%!important', margin: '0!important', padding: '0!important'} : { width: '900px' }" 
        :contentStyle="(screenSize=='xs' || screenSize=='sm' || screenSize=='md') ? { height: '100%' } : { height: 'auto' }"
        :class="(screenSize=='xs') ? 'h-full border-noround-bottom' : ''">

        <template #header>
            <div class="flex flex-column w-full mb-3">
                <span class="flex text-lg font-medium">Редактировать банк <span v-if="currentBank.code">{{ currentBank.code }}</span></span>
            </div>
        </template>
        
        <div class="flex flex-column w-full gap-3">
            <div class="flex flex-column xl:flex-row lg:flex-row w-full gap-3">
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Тип платежной системы <span class="text-red-500">*</span>: </span>
                    <Dropdown v-model="currentBank.type" :options="types" optionLabel="label" optionValue="value" placeholder="Тип" class="flex" />
                </div>
                <div class="flex flex-column w-full gap-2" >
                    <span class="flex">Код <span class="text-red-500">*</span>: </span>
                    <InputText v-model="currentBank.code" class="flex" placeholder="sber_rub_atm"/>
                </div>
            </div>

            <div class="flex flex-column xl:flex-row lg:flex-row w-full gap-3">
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Активность: </span>
                    <SelectButton v-model="currentBank.active" :options="active_list" optionLabel="name" optionValue="value" aria-labelledby="basic" />
                </div>
                <div class="flex flex-column w-full gap-2" >
                    <span class="flex">Картинка <span class="text-red-500">*</span>: </span>
                    <InputText v-model="currentBank.image" class="flex" placeholder="/images/svg/banks/other.svg"/>
                </div>
            </div>

            <div class="flex flex-column xl:flex-row lg:flex-row w-full gap-3">
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Сортировка <span class="text-red-500">*</span>: </span>
                    <InputNumber v-model="currentBank.sort" class="flex"/>
                </div>
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Валюта <span class="text-red-500">*</span>: </span>
                    <Select v-model="currentBank.currency_code" :options="currencies" placeholder="Валюта" optionLabel="code" optionValue="code" :showClear="true" filter class="flex w-full">
                        <template #value="slotProps">
                            <div v-if="slotProps.value" class="flex align-items-center"> 
                                <img :src="getCurImage(slotProps.value)" class="mr-2 w-3 border-200 border-1 border-circle" style="max-width: 22px;" />
                                <span class="flex uppercase ml-2">{{slotProps.value}}</span>
                            </div>
                            <span v-else>
                                {{ slotProps.placeholder }}
                            </span>
                        </template>
                        <template #option="slotProps">
                            <div class="flex align-items-center">
                                <img v-if="slotProps.option.code" :src="getCurImage(slotProps.option.code)" class="mr-2 w-3 border-200 border-1 border-circle" style="max-width: 22px;" />
                                <span class="flex uppercase ml-2">{{slotProps.option.code}}</span>
                            </div>
                        </template>
                    </Select>
                </div>
            </div>

            <div class="flex flex-column xl:flex-row lg:flex-row w-full gap-3">
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Название <span class="text-red-500">*</span>: </span>
                    <InputText v-model="currentBank.titles.ru" class="flex" placeholder="Сбер"/>
                </div>
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Название на английском <span class="text-red-500">*</span>: </span>
                    <InputText v-model="currentBank.titles.en" class="flex" placeholder="Sberbank"/>
                </div>
            </div>

            <div class="flex flex-column xl:flex-row lg:flex-row w-full gap-3">
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Описание: </span>
                    <InputText v-model="currentBank.descriptions.ru" class="flex" placeholder="Пополнение и вывод на карту Сбербанка"/>
                </div>
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Описание на английском: </span>
                    <InputText v-model="currentBank.descriptions.en" class="flex" placeholder="Deposit and withdrawal to Sberbank card"/>
                </div>
            </div>

            <div class="flex flex-column xl:flex-row lg:flex-row w-full gap-3">
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Фикс. комиссия: </span>
                    <InputNumber v-if="currentBank.currency_code" v-model="currentBank.comissions.fix" class="flex" :minFractionDigits="0" :maxFractionDigits="2" fluid placeholder="1"/>
                    <InputNumber v-else v-model="currentBank.comissions.fix" class="flex" :minFractionDigits="0" :maxFractionDigits="2" fluid placeholder="1"/>
                </div>
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Процентная комиссия (до 50%): </span>
                    <InputNumber v-model="currentBank.comissions.percent" class="flex" :minFractionDigits="0" :maxFractionDigits="2" :min="0" :max="0.5" fluid placeholder="0.05"/>
                </div>
            </div>

            <div class="flex flex-column xl:flex-row lg:flex-row w-full gap-3">
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Минимальный лимит <span class="text-red-500">*</span>: </span>
                    <InputNumber v-if="currentBank.currency_code" v-model="currentBank.limits.min" class="flex" :minFractionDigits="0" :maxFractionDigits="2" :min="0" :max="999999999" placeholder="100"/>
                    <InputNumber v-else v-model="currentBank.limits.min" class="flex" :maxFractionDigits="2" :min="0" :max="999999999" placeholder="100"/>
                </div>
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Максимальный лимит <span class="text-red-500">*</span>: </span>
                    <InputNumber v-if="currentBank.currency_code" v-model="currentBank.limits.max" class="flex" :maxFractionDigits="2" :min="0" :max="999999999" placeholder="1000000"/>
                    <InputNumber v-else v-model="currentBank.limits.max" class="flex" :maxFractionDigits="2" :min="0" :max="999999999" placeholder="1000000"/>
                </div>
            </div>

            <div class="flex flex-column w-full gap-2">
                <span class="flex">Поля получателя <span class="text-red-500">*</span>: </span>
                <MultiSelect v-model="currentBank.recipient_fields" :options="fields" placeholder="Выберите до 3 полей"
                    :maxSelectedLabels="3" class="w-full" />
            </div>

            <div class="flex flex-column w-full gap-2">
                <span class="flex">Обязательные поля получателя <span class="text-red-500">*</span>: </span>
                <MultiSelect v-model="currentBank.recipient_fields_required" :options="fields" placeholder="Выберите до 3 полей"
                    :maxSelectedLabels="3" class="w-full" />
            </div>

            <div class="flex flex-column w-full gap-2">
                <span class="flex">Время в мс <span class="text-red-500">*</span>: </span>
                <InputNumber v-model="currentBank.time" class="flex" :maxFractionDigits="0" :min="1" :max="999999999" placeholder="10000" :useGrouping="false"/>
                <div class="flex flex-row w-full gap-1"
                    :class="(screenSize === 'xs' || screenSize === 'sm') ? 'overflow-x-scroll' : ''">
                    <div v-for="tr of timeRanges" class="flex p-1 border-2 border-200 border-round-md surface-0 cursor-pointer"
                        @click="currentBank.time=tr.value">
                        <span class="text-xs text-500 inline-block white-space-nowrap float-left m-0" >{{ tr.name }}</span>
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="w-full mb-3 flex justify-content-between flex-wrap">
                <Button label="Изменить" @click='updBank()' :loading="loading" class="flex" :disabled="loading|| !isEditFormValid"/>
                <Button label="Закрыть" icon="pi pi-times" @click="closeDialog" outlined class="flex" autofocus severity="secondary" :disabled="loading"/>
            </div>
        </template>
        
    </Dialog>

    <!--Добавление-->
    <Dialog class="flex flex-column border-none" modal
        v-model:visible="addPopup"
        :position="screenSize=='xs' ? 'bottom' : 'center'"
        :style="(screenSize=='xs' || screenSize=='sm' || screenSize=='md') ? {width: '100%!important', height: '100%!important', margin: '0!important', padding: '0!important'} : { width: '900px' }" 
        :contentStyle="(screenSize=='xs' || screenSize=='sm' || screenSize=='md') ? { height: '100%' } : { height: 'auto' }"
        :class="(screenSize=='xs') ? 'h-full border-noround-bottom' : ''">

        <template #header>
            <div class="flex flex-column w-full mb-3">
                <span class="flex text-lg font-medium">Добавить банк</span>
            </div>
        </template>
        
        <div class="flex flex-column w-full gap-3">
            <div class="flex flex-column xl:flex-row lg:flex-row w-full gap-3">
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Тип платежной системы <span class="text-red-500">*</span>: </span>
                    <Dropdown v-model="newBank.type" :options="types" optionLabel="label" optionValue="value" placeholder="Тип" class="flex" />
                </div>
                <div class="flex flex-column w-full gap-2" >
                    <span class="flex">Код <span class="text-red-500">*</span>: </span>
                    <InputText v-model="newBank.code" class="flex" placeholder="sber_rub_atm"/>
                </div>
            </div>

            <div class="flex flex-column xl:flex-row lg:flex-row w-full gap-3">
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Активность: </span>
                    <SelectButton v-model="newBank.active" :options="active_list" optionLabel="name" optionValue="value" aria-labelledby="basic" />
                </div>
                <div class="flex flex-column w-full gap-2" >
                    <span class="flex">Картинка <span class="text-red-500">*</span>: </span>
                    <InputText v-model="newBank.image" class="flex" placeholder="/images/svg/banks/other.svg"/>
                </div>
            </div>

            <div class="flex flex-column xl:flex-row lg:flex-row w-full gap-3">
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Сортировка <span class="text-red-500">*</span>: </span>
                    <InputNumber v-model="newBank.sort" class="flex"/>
                </div>
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Валюта <span class="text-red-500">*</span>: </span>
                    <Select v-model="newBank.currency_code" :options="currencies" placeholder="Валюта" optionLabel="code" optionValue="code" :showClear="true" filter class="flex w-full">
                        <template #value="slotProps">
                            <div v-if="slotProps.value" class="flex align-items-center"> 
                                <img :src="getCurImage(slotProps.value)" class="mr-2 w-3 border-200 border-1 border-circle" style="max-width: 22px;" />
                                <span class="flex uppercase ml-2">{{slotProps.value}}</span>
                            </div>
                            <span v-else>
                                {{ slotProps.placeholder }}
                            </span>
                        </template>
                        <template #option="slotProps">
                            <div class="flex align-items-center">
                                <img v-if="slotProps.option.code" :src="getCurImage(slotProps.option.code)" class="mr-2 w-3 border-200 border-1 border-circle" style="max-width: 22px;" />
                                <span class="flex uppercase ml-2">{{slotProps.option.code}}</span>
                            </div>
                        </template>
                    </Select>
                </div>
            </div>

            <div class="flex flex-column xl:flex-row lg:flex-row w-full gap-3">
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Название <span class="text-red-500">*</span>: </span>
                    <InputText v-model="newBank.titles.ru" class="flex" placeholder="Сбер"/>
                </div>
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Название на английском <span class="text-red-500">*</span>: </span>
                    <InputText v-model="newBank.titles.en" class="flex" placeholder="Sberbank"/>
                </div>
            </div>

            <div class="flex flex-column xl:flex-row lg:flex-row w-full gap-3">
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Описание: </span>
                    <InputText v-model="newBank.descriptions.ru" class="flex" placeholder="Пополнение и вывод на карту Сбербанка"/>
                </div>
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Описание на английском: </span>
                    <InputText v-model="newBank.descriptions.en" class="flex" placeholder="Deposit and withdrawal to Sberbank card"/>
                </div>
            </div>

            <div class="flex flex-column xl:flex-row lg:flex-row w-full gap-3">
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Фикс. комиссия: </span>
                    <InputNumber v-if="newBank.currency_code" v-model="newBank.comissions.fix" class="flex" :minFractionDigits="0" :maxFractionDigits="2" fluid placeholder="1"
                        :currency="newBank.currency_code" mode="currency"/>
                    <InputNumber v-else v-model="newBank.comissions.fix" class="flex" :minFractionDigits="0" :maxFractionDigits="2" fluid placeholder="1"/>
                </div>
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Процентная комиссия (до 50%): </span>
                    <InputNumber v-model="newBank.comissions.percent" class="flex" :minFractionDigits="0" :maxFractionDigits="2" :min="0" :max="0.5" fluid placeholder="0.05"/>
                </div>
            </div>

            <div class="flex flex-column xl:flex-row lg:flex-row w-full gap-3">
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Минимальный лимит <span class="text-red-500">*</span>: </span>
                    <InputNumber v-if="newBank.currency_code" v-model="newBank.limits.min" class="flex" :minFractionDigits="0" :maxFractionDigits="2" :min="0" :max="999999999" placeholder="100"
                        :currency="newBank.currency_code" mode="currency"/>
                    <InputNumber v-else v-model="newBank.limits.min" class="flex" :maxFractionDigits="2" :min="0" :max="999999999" placeholder="100"/>
                </div>
                <div class="flex flex-column w-full gap-2">
                    <span class="flex">Максимальный лимит <span class="text-red-500">*</span>: </span>
                    <InputNumber v-if="newBank.currency_code" v-model="newBank.limits.max" class="flex" :maxFractionDigits="2" :min="0" :max="999999999" placeholder="1000000"
                        :currency="newBank.currency_code" mode="currency"/>
                    <InputNumber v-else v-model="newBank.limits.max" class="flex" :maxFractionDigits="2" :min="0" :max="999999999" placeholder="1000000"/>
                </div>
            </div>

            <div class="flex flex-column w-full gap-2">
                <span class="flex">Поля получателя <span class="text-red-500">*</span>: </span>
                <MultiSelect v-model="newBank.recipient_fields" :options="fields" placeholder="Выберите до 3 полей"
                    :maxSelectedLabels="3" class="w-full" />
            </div>

            <div class="flex flex-column w-full gap-2">
                <span class="flex">Обязательные поля получателя <span class="text-red-500">*</span>: </span>
                <MultiSelect v-model="newBank.recipient_fields_required" :options="fields" placeholder="Выберите до 3 полей"
                    :maxSelectedLabels="3" class="w-full" />
            </div>

            <div class="flex flex-column w-full gap-2">
                <span class="flex">Время в мс <span class="text-red-500">*</span>: </span>
                <InputNumber v-model="newBank.time" class="flex" :maxFractionDigits="0" :min="1" :max="999999999" :placeholder="10000" :useGrouping="false"/>
                <div class="flex flex-row w-full gap-1"
                    :class="(screenSize === 'xs' || screenSize === 'sm') ? 'overflow-x-scroll' : ''">
                    <div v-for="tr of timeRanges" class="flex p-1 border-2 border-200 border-round-md surface-0 cursor-pointer"
                        @click="newBank.time=tr.value">
                        <span class="text-xs text-500 inline-block white-space-nowrap float-left m-0" >{{ tr.name }}</span>
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="w-full flex justify-content-between flex-wrap">
                <Button label="Добавить" @click='addBank()' :loading="loading" class="flex" :disabled="loading|| !isAddFormValid"/>
                <Button label="Закрыть" icon="pi pi-times" @click="closeDialog" outlined class="flex" autofocus severity="secondary" :disabled="loading"/>
            </div>
        </template>
    </Dialog>
</template>

<script>
import Connect from '@/assets/js/Connect'
import Helper from '@/assets/js/Helper'
import { BankList } from '@/assets/js/classes/PaymentSystem.js'
import { CurrencyList } from '@/assets/js/classes/Currency.js'

export default {
    data() {
        return {
            banks: [],
            currentBank: {},
            newBank: {
                type: '',
                code: '',
                active: true,
                image: '/images/svg/banks/other.svg',
                sort: 1,
                currency_code: '',
                titles: {ru: '', en: ''},
                descriptions: {ru: '', en: ''},
                comissions: {fix: null, percent: null},
                limits: {min: null, max: null},
                recipient_fields: [],
                recipient_fields_required: [],
                time: 1,
            },
            currencies: [],
            currenciesCodes: [],
            editPopup: false,
            addPopup: false,
            loading: false,
            lazyParams: {},
            types: [
                { label: 'Карта', value: 'card'},
                { label: 'Биржа', value: 'stock'},
                { label: 'СБП', value: 'sbp'},
                { label: 'Банкомат', value: 'atm'},
                { label: 'Курьер', value: 'courier'},
                { label: 'SWIFT', value: 'swift'},
                { label: 'Офис', value: 'office'},
                { label: 'Крипта', value: 'crypto'},
                { label: 'В чате', value: 'chat'},
            ],
            fields: ['fullname', 'phone_number', 'card_number', 'account_number', 'wallet_address', 'wallet_network', 'telegram', 'bank_name', 'address', 'city', 'country', 'swift_code', 'iban_number', 'upi_id', 'file'],
            filters: {
                id: {value: '', matchMode: 'contains'}, 
                active: {value: null, matchMode: 'equals'},
                type: {value: '', matchMode: 'contains'},
                currency_code: {value: '', matchMode: 'contains'},
                code: {value: '', matchMode: 'contains'},
            },
            active_list: [
                {name: 'Активен', value: true},
                {name: 'Не активен', value: false}
            ],
            selectedColumns: [],
            sets: [
                {name: 'Комиссии', value: 'Комиссии'},
                {name: 'Лимиты', value: 'Лимиты'},
                {name: 'Суммы', value: 'Суммы'},
                {name: 'Реквизиты', value: 'Реквизиты'},
            ],
            set: [],
            timeRanges: [
                {name: '15 мин', value: 900000},
                {name: '30 мин', value: 1800000},
                {name: '1 час', value: 3600000},
                {name: '3 часа', value: 10800000},
                {name: '12 часов', value: 43200000},
                {name: '24 часа', value: 86400000},
                {name: '2 дня', value: 172800000},
                {name: '3 дня', value: 259200000},
            ]
        }
    },
    watch: {
        editPopup(val) {
            if(!val){
                this.currentBank = {}
            }
        },
        set: {
            handler(val) { 
                this.selectedColumns = []
                for (let i = 0; i < val.length; i++) {
                    let v = val[i].value
                    if(Array.isArray(v)) this.selectedColumns = this.selectedColumns.concat(v)
                    else this.selectedColumns.push(v)
                }
            },
            immediate: true
        },
    },
    computed: {
        lang_() { return (this.$i18n.locale) ? this.$i18n.locale : 'ru' },
        screenSize() { return this.$store.getters.StateScreenSize },
        isAddFormValid() {
            if(!this.newBank.type) return false
            if(!this.newBank.code) return false
            if(!this.newBank.image) return false
            if(!this.newBank.currency_code) return false
            if(!this.newBank.titles.ru) return false
            if(!this.newBank.titles.en) return false
            if(!this.newBank.limits) return false
            if(!this.newBank.limits.min && this.newBank.limits.min!==0) return false
            if(!this.newBank.limits.max && this.newBank.limits.max!==0) return false
            if(!this.newBank.recipient_fields || !this.newBank.recipient_fields.length) return false
            if(!this.newBank.recipient_fields_required || !this.newBank.recipient_fields_required.length) return false
            if(!this.newBank.time) return false
            return true
        },
        isEditFormValid() {
            if(!this.currentBank.type) return false
            if(!this.currentBank.code) return false
            if(!this.currentBank.image) return false
            if(!this.currentBank.currency_code) return false
            if(!this.currentBank.titles.ru) return false
            if(!this.currentBank.titles.en) return false
            if(!this.currentBank.limits) return false
            if(!this.currentBank.limits.min && this.currentBank.limits.min!==0) return false
            if(!this.currentBank.limits.max && this.currentBank.limits.max!==0) return false
            if(!this.currentBank.recipient_fields || !this.currentBank.recipient_fields.length) return false
            if(!this.currentBank.recipient_fields_required || !this.currentBank.recipient_fields_required.length) return false
            if(!this.currentBank.time) return false
            return true
        },
    },
    methods: {
        copy(text){
            this.helper.copy_clipboard(text)
            this.$toast.add({ severity: 'info', summary: 'Скопировано', detail: text, life: 1000 })
        },
        isUserInGroup: function (type) { 
            return this.helper.isUserInGroup(type, this.$store.getters.StateGroups)
        },
        onPage(event) {
            this.lazyParams = event
            window.scrollTo({ top: 0, behavior: 'smooth' })
        },
        onSort(event) {
            this.lazyParams = event
        },
        onFilter() {
            this.lazyParams.filters = this.filters
            window.scrollTo({ top: 0, behavior: 'smooth' })
        },
        onRowSelect() {
            this.selectAll = this.selectedCustomers.length === this.totalRecords
        },
        onRowUnselect() {
            this.selectAll = false;
        },
        get_reverce_rate(rate){
            return this.helper.get_reverce_rate(rate)
        },
        closeDialog(){
            this.editPopup = false
            this.addPopup = false
            this.currentBank = {}
        },
        getCurImage(code){
            return this.helper.GetCurrencyImage(code, this.currencies)
        },
        formatDate(date) {
            return this.helper.format_date_short(date)
        },
        getBankTitle(code){
            let bankObj = new BankList()
            bankObj.items = this.banks
            const bankCode = bankObj.GetTitle(code, this.lang_)
            return (bankCode) ? bankCode : code
        },
        getBankImage(code){
            let bankObj = new BankList()
            bankObj.items = this.banks
            const bankCode = bankObj.GetImage(code)
            return (bankCode) ? bankCode : code
        },
        getBankDescription(code){
            let bankObj = new BankList()
            bankObj.items = this.banks
            const bankCode = bankObj.GetDescription(code, this.lang_)
            return (bankCode) ? bankCode : code
        },
        showMess(severity, summary, detail) {
            this.$toast.add({severity: severity, summary: summary, detail: detail, life: 3000});
        },
        getType(type){
            const t = this.types.find(t=>t.value==type)
            return (t) ? t.label : type
        },
        getBank(code){
            if(!this.banks) return false
            if(!code) return false
            return this.banks.find(bank=>bank.code==code)
        },
        edit(code){
            this.editPopup = true
            this.currentBank = this.getBank(code)
            if(!this.currentBank){
                this.showMess('warn', 'Внимание', 'Не найден банк')
                return false
            }
        },
        openAddPopup(){
            this.addPopup = true
        },
        get_format_amount(amount, currency){
            return this.helper.format_amount(amount, currency)
        },
        getFieldIcon(field){
            return this.helper.get_field_icon(field)
        },
        async updBank(){
            this.loading = true
            try{
                const data = {
                    id:                         this.currentBank.id,
                    type:                       this.currentBank.type,
                    code:                       this.currentBank.code,
                    active:                     this.currentBank.active,
                    image:                      this.currentBank.image,
                    sort:                       this.currentBank.sort,
                    currency_code:              this.currentBank.currency_code,
                    titles:                     this.currentBank.titles,
                    descriptions:               this.currentBank.descriptions,
                    comissions:                 this.currentBank.comissions,
                    limits:                     this.currentBank.limits,
                    recipient_fields:           this.currentBank.recipient_fields,
                    recipient_fields_required:  this.currentBank.recipient_fields_required,
                    time:                       this.currentBank.time,
                }
                const upd = await this.connect.bank_upd_by_admin(data).then(res=>res)
                if(upd.result && upd.data){
                    this.showMess('success', 'Успешно', 'Банк обновлен')
                    this.editPopup = false
                    this.currentBank = {}
                    this.loading = false
                } else {
                    try{
                        for (let i = 0; i < upd.errors.length; i++) this.showMess('error', 'Ошибка', upd.errors[i])
                    }catch(e){
                        this.showMess('error', 'Ошибка', 'Ошибка при изменении банка')
                        console.log('error', e)
                    }
                    this.loading = false
                }
            } catch(e){
                this.showMess('error', 'Ошибка', 'Ошибка при изменении банка')
                console.log('error', e)
                this.loading = false
            }
        },
        async addBank(){
            this.loading = true
            try{
                const add = await this.connect.bank_add_by_admin(this.newBank).then(res=>res)
                if(add.result && add.data){
                    this.showMess('success', 'Успешно', 'Банк добавлен')
                    this.addPopup = false
                    this.newBank = {
                        type: '',
                        code: '',
                        active: true,
                        image: '',
                        sort: 0,
                        currency_code: '',
                        titles: {ru: '', en: ''},
                        descriptions: {ru: '', en: ''},
                        comissions: {fix: 0, percent: 0},
                        limits: {min: 0, max: 0},
                        recipient_fields: [],
                        recipient_fields_required: [],
                        time: 0,
                    }
                    this.loading = false
                } else {
                    try{
                        for (let i = 0; i < add.errors.length; i++) {
                            this.showMess('error', 'Ошибка', add.errors[i])
                        }
                    }catch(e){
                        this.showMess('error', 'Ошибка', 'Ошибка при добавлении банка')
                        console.log('error', e)
                    }
                    this.loading = false
                } 
            } catch(e){
                this.showMess('error', 'Ошибка', 'Ошибка при добавлении банка')
                console.log('error', e)
                this.loading = false
            }  
        },
        async LoadBanks(){
            this.loading = true
            try { 
                this.banks = await BankList.FetchFromApi(this.$store).then(r=>r)
                if(!this.banks || !this.banks.length) this.banks = []
                else this.totalRecords = this.banks.length
            } catch { console.log('error store get banks')}
            this.loading = false
        },
        async LoadCurrencies(){
            this.currencies = await CurrencyList.GetList(this.$store).then(r=>r)
            this.currenciesCodes = this.currencies.map(c=>c.code)
        },
        resetFilter(){
            this.selectedColumns = []
            this.set = []
            this.filters = {
                id: {value: '', matchMode: 'contains'}, 
                active: {value: null, matchMode: 'equals'},
                code: {value: '', matchMode: 'contains'},
                type: {value: '', matchMode: 'contains'},
                currency_code: {value: '', matchMode: 'contains'},
            }
            this.LoadBanks().then()
        },
    },
    async beforeCreate() {
        this.connect = new Connect()
        this.helper = new Helper()
    },
    async mounted() {
        this.lazyParams = {
            first: 0,
            // rows: dt.value.rows,
            rows: 10,
            sortField: null,
            sortOrder: null,
            filters: this.filters,
        };
        await this.LoadCurrencies().then()
        await this.LoadBanks().then()
    },
}
</script>